import { useState, useEffect } from 'react';
import { Popconfirm, Checkbox, DatePicker, message } from 'antd';
import { LuTrash2, LuPlus, LuX, LuPencil } from 'react-icons/lu';
import { motion } from 'motion/react';
import dayjs from 'dayjs';
import { darkenHex, lightenHex, accentRgba } from './utils.js';

const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

function dayjsToDateObject(d) {
	if (!d) return null;
	return {
		month: d.month(),
		year: d.year(),
		monthName: d.format('MMMM'),
		fullString: d.format('YYYY-MM-DD'),
	};
}

function dateObjectToDayjs(obj) {
	if (!obj?.fullString) return null;
	const parsed = dayjs(obj.fullString);
	return parsed.isValid() ? parsed : null;
}

function formatRange(job) {
	const start = job.start
		? `${job.start.monthName || MONTHS[job.start.month]} ${job.start.year}`
		: '';
	let end = '';
	if (job.end) {
		if (job.end.current) end = 'Present';
		else end = `${job.end.monthName || MONTHS[job.end.month]} ${job.end.year}`;
	}
	return [start, end].filter(Boolean).join(' – ');
}

function jobSortKey(job) {
	const s = job?.start;
	if (!s) return -Infinity;
	const year = typeof s.year === 'number' ? s.year : 0;
	const month = typeof s.month === 'number' ? s.month : 0;
	return new Date(year, month, 1).getTime();
}

function sortByStartAsc(list) {
	return [...list].sort((a, b) => jobSortKey(a) - jobSortKey(b));
}

function fieldStyle() {
	return {
		padding: '10px 16px',
		background: '#fff',
		border: '1px solid #e2e8f0',
		borderRadius: 12,
		outline: 'none',
		color: '#334155',
		fontSize: 14,
		width: '100%',
		transition: 'all 0.2s',
	};
}

function applyFocus(accentColor) {
	return (e) => {
		e.target.style.borderColor = lightenHex(accentColor);
		e.target.style.boxShadow = `0 0 0 3px ${accentRgba(accentColor, 0.1)}`;
	};
}

function applyBlur() {
	return (e) => {
		e.target.style.borderColor = '#e2e8f0';
		e.target.style.boxShadow = 'none';
	};
}

function PositionForm({
	title: formTitle,
	submitLabel,
	initialJob,
	onSubmit,
	onCancel,
	accentColor,
}) {
	const [title, setTitle] = useState(initialJob?.title || '');
	const [name, setName] = useState(initialJob?.name || '');
	const [start, setStart] = useState(() => dateObjectToDayjs(initialJob?.start));
	const [end, setEnd] = useState(() => dateObjectToDayjs(initialJob?.end));
	const [current, setCurrent] = useState(!!initialJob?.end?.current);
	const [description, setDescription] = useState(initialJob?.description || '');

	const submit = () => {
		if (!title.trim()) {
			message.error('Job title is required');
			return;
		}
		if (!start) {
			message.error('Start date is required');
			return;
		}
		const job = {
			title: title.trim(),
			name: name.trim(),
			description: description.trim(),
			start: dayjsToDateObject(start),
			end: current ? { current: true } : dayjsToDateObject(end),
		};
		onSubmit(job);
	};

	return (
		<div
			className="rounded-xl"
			style={{
				padding: 16,
				background: '#f8fafc',
				border: '1px solid #e2e8f0',
				display: 'flex',
				flexDirection: 'column',
				gap: 12,
			}}
		>
			<h3
				style={{
					fontSize: 14,
					fontWeight: 600,
					color: '#334155',
					margin: 0,
				}}
			>
				{formTitle}
			</h3>
			<input
				type="text"
				placeholder="Job title *"
				value={title}
				onChange={(e) => setTitle(e.target.value)}
				style={fieldStyle()}
				onFocus={applyFocus(accentColor)}
				onBlur={applyBlur()}
			/>
			<input
				type="text"
				placeholder="Organization"
				value={name}
				onChange={(e) => setName(e.target.value)}
				style={fieldStyle()}
				onFocus={applyFocus(accentColor)}
				onBlur={applyBlur()}
			/>
			<div className="flex flex-col gap-2 sm:flex-row">
				<div style={{ flex: 1 }}>
					<DatePicker
						value={start}
						onChange={setStart}
						format="YYYY-MM-DD"
						placeholder="Start date *"
						style={{ width: '100%', borderRadius: 12 }}
						allowClear
					/>
				</div>
				<div style={{ flex: 1 }}>
					<DatePicker
						value={end}
						onChange={setEnd}
						format="YYYY-MM-DD"
						placeholder="End date"
						style={{ width: '100%', borderRadius: 12 }}
						allowClear
						disabled={current}
					/>
				</div>
			</div>
			<Checkbox
				checked={current}
				onChange={(e) => {
					setCurrent(e.target.checked);
					if (e.target.checked) setEnd(null);
				}}
			>
				I currently work here
			</Checkbox>
			<textarea
				rows={3}
				placeholder="Description"
				value={description}
				onChange={(e) => setDescription(e.target.value)}
				style={{ ...fieldStyle(), resize: 'vertical' }}
				onFocus={applyFocus(accentColor)}
				onBlur={applyBlur()}
			/>
			<div className="flex justify-end gap-2">
				<button
					type="button"
					onClick={onCancel}
					style={{
						padding: '8px 14px',
						background: '#f1f5f9',
						color: '#475569',
						border: 'none',
						borderRadius: 10,
						fontSize: 13,
						fontWeight: 600,
						cursor: 'pointer',
					}}
				>
					Cancel
				</button>
				<button
					type="button"
					onClick={submit}
					style={{
						padding: '8px 14px',
						background: accentColor,
						color: '#fff',
						border: 'none',
						borderRadius: 10,
						fontSize: 13,
						fontWeight: 600,
						cursor: 'pointer',
						transition: 'background 0.2s',
					}}
					onMouseOver={(e) => {
						e.currentTarget.style.background = darkenHex(accentColor);
					}}
					onMouseOut={(e) => {
						e.currentTarget.style.background = accentColor;
					}}
				>
					{submitLabel}
				</button>
			</div>
		</div>
	);
}

function PositionRow({ job, accentColor, onEdit, onDelete }) {
	return (
		<div
			className="flex items-start justify-between gap-3 rounded-xl"
			style={{
				padding: 14,
				border: '1px solid #e2e8f0',
				background: '#fff',
			}}
		>
			<div style={{ minWidth: 0, flex: 1 }}>
				<p
					style={{
						margin: 0,
						fontSize: 14,
						fontWeight: 600,
						color: '#1e293b',
					}}
				>
					{job.title}
				</p>
				{job.name && (
					<p
						style={{
							margin: '2px 0 0',
							fontSize: 13,
							color: '#475569',
						}}
					>
						{job.name}
					</p>
				)}
				<p
					style={{
						margin: '2px 0 0',
						fontSize: 12,
						color: '#475569',
					}}
				>
					{formatRange(job)}
				</p>
				{job.description && (
					<p
						style={{
							margin: '6px 0 0',
							fontSize: 12,
							color: '#475569',
							lineHeight: 1.5,
						}}
					>
						{job.description}
					</p>
				)}
			</div>
			<div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
				<button
					type="button"
					onClick={onEdit}
					className="flex items-center justify-center"
					style={{
						width: 28,
						height: 28,
						borderRadius: 8,
						border: 'none',
						background: 'transparent',
						color: '#475569',
						cursor: 'pointer',
					}}
					onMouseOver={(e) => {
						e.currentTarget.style.background = accentRgba(accentColor, 0.1);
						e.currentTarget.style.color = accentColor;
					}}
					onMouseOut={(e) => {
						e.currentTarget.style.background = 'transparent';
						e.currentTarget.style.color = '#475569';
					}}
					title="Edit"
				>
					<LuPencil size={14} />
				</button>
				<Popconfirm
					title="Remove this position?"
					okText="Remove"
					okType="danger"
					cancelText="Cancel"
					onConfirm={onDelete}
				>
					<button
						type="button"
						className="flex items-center justify-center"
						style={{
							width: 28,
							height: 28,
							borderRadius: 8,
							border: 'none',
							background: 'transparent',
							color: '#475569',
							cursor: 'pointer',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.background = 'rgba(220,38,38,0.08)';
							e.currentTarget.style.color = '#dc2626';
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.background = 'transparent';
							e.currentTarget.style.color = '#475569';
						}}
						title="Remove"
					>
						<LuTrash2 size={14} />
					</button>
				</Popconfirm>
			</div>
		</div>
	);
}

function WorkExperienceModal({
	onClose,
	employment,
	onChangeEmployment,
	accentColor,
}) {
	const [addingNew, setAddingNew] = useState(false);
	const [editingIndex, setEditingIndex] = useState(null);

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		const handleEsc = (e) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleEsc);
		return () => {
			document.body.style.overflow = '';
			document.removeEventListener('keydown', handleEsc);
		};
	}, [onClose]);

	const sortedEmployment = sortByStartAsc(employment);

	const handleAdd = (job) => {
		onChangeEmployment(sortByStartAsc([...employment, job]));
		setAddingNew(false);
	};

	const handleUpdate = (job) => {
		const updated = sortedEmployment.map((existing, i) =>
			i === editingIndex ? job : existing
		);
		onChangeEmployment(sortByStartAsc(updated));
		setEditingIndex(null);
	};

	const handleDelete = (index) => {
		const next = sortedEmployment.filter((_, i) => i !== index);
		onChangeEmployment(sortByStartAsc(next));
	};

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4 work-exp-modal"
			onClick={onClose}
		>
			<style>{`.work-exp-modal input, .work-exp-modal textarea { background-color: #fff !important; }`}</style>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className="fixed inset-0"
				style={{
					background: 'rgba(15,23,42,0.5)',
					backdropFilter: 'blur(4px)',
				}}
			/>

			<motion.div
				initial={{ opacity: 0, scale: 0.95 }}
				animate={{ opacity: 1, scale: 1 }}
				exit={{ opacity: 0, scale: 0.95 }}
				transition={{ duration: 0.3, ease: 'easeOut' }}
				className="relative flex w-full flex-col overflow-hidden rounded-2xl bg-white"
				style={{
					maxWidth: 672,
					maxHeight: '90vh',
					boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				<div
					className="flex items-center justify-between p-6"
					style={{ paddingBottom: 0 }}
				>
					<h2
						style={{
							color: '#1e293b',
							fontSize: 18,
							fontWeight: 600,
							margin: 0,
						}}
					>
						Manage Work Experience
					</h2>
					<button
						type="button"
						onClick={onClose}
						style={{
							width: 32,
							height: 32,
							borderRadius: 12,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: 0,
							lineHeight: 0,
							color: '#475569',
							background: 'none',
							border: 'none',
							cursor: 'pointer',
							transition: 'all 0.2s',
						}}
						onMouseOver={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = '#f1f5f9';
						}}
						onMouseOut={(e) => {
							e.currentTarget.style.color = '#475569';
							e.currentTarget.style.background = 'none';
						}}
					>
						<LuX size={14} />
					</button>
				</div>

				<div
					className="overflow-y-auto p-6"
					style={{
						scrollbarWidth: 'thin',
						scrollbarColor: '#cbd5e1 transparent',
					}}
				>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
						{sortedEmployment.length > 0 && (
							<div>
								<div
									className="mb-2 flex items-center justify-between"
									style={{ color: '#334155', fontSize: 14, fontWeight: 600 }}
								>
									<span>Your positions</span>
									<Popconfirm
										title="Remove all work experience?"
										okText="Remove All"
										cancelText="Cancel"
										onConfirm={() => onChangeEmployment([])}
									>
										<button
											type="button"
											style={{
												background: 'none',
												border: 'none',
												color: '#dc2626',
												fontSize: 12,
												fontWeight: 600,
												cursor: 'pointer',
												padding: 0,
											}}
										>
											Remove All
										</button>
									</Popconfirm>
								</div>
								<div className="flex flex-col gap-2">
									{sortedEmployment.map((job, index) =>
										editingIndex === index ? (
											<PositionForm
												key={`edit-${index}`}
												title="Update position"
												submitLabel="Update Position"
												initialJob={job}
												onSubmit={handleUpdate}
												onCancel={() => setEditingIndex(null)}
												accentColor={accentColor}
											/>
										) : (
											<PositionRow
												key={`${job.title}-${index}`}
												job={job}
												accentColor={accentColor}
												onEdit={() => {
													setAddingNew(false);
													setEditingIndex(index);
												}}
												onDelete={() => handleDelete(index)}
											/>
										)
									)}
								</div>
							</div>
						)}

						{sortedEmployment.length === 0 && !addingNew && (
							<p
								style={{
									color: '#475569',
									fontSize: 13,
									textAlign: 'center',
									margin: 0,
									padding: '16px 0',
								}}
							>
								No work experience yet
							</p>
						)}

						{addingNew ? (
							<PositionForm
								title="Add a position"
								submitLabel="Add Position"
								onSubmit={handleAdd}
								onCancel={() => setAddingNew(false)}
								accentColor={accentColor}
							/>
						) : (
							editingIndex === null && (
								<button
									type="button"
									onClick={() => setAddingNew(true)}
									className="flex items-center justify-center gap-2"
									style={{
										padding: '10px 14px',
										background: accentRgba(accentColor, 0.08),
										color: accentColor,
										border: `1px dashed ${accentRgba(accentColor, 0.35)}`,
										borderRadius: 12,
										fontSize: 13,
										fontWeight: 600,
										cursor: 'pointer',
									}}
								>
									<LuPlus size={14} />
									Add a position
								</button>
							)
						)}

						<p
							style={{
								fontSize: 12,
								color: '#475569',
								margin: 0,
								fontStyle: 'italic',
							}}
						>
							Changes are saved when you click "Update Profile".
						</p>

						<div className="flex gap-3 pt-2">
							<button
								type="button"
								onClick={onClose}
								style={{
									flex: 1,
									paddingTop: 10,
									paddingBottom: 10,
									borderRadius: '0.75rem',
									fontSize: 14,
									fontWeight: 600,
									background: '#f1f5f9',
									color: '#475569',
									border: 'none',
									cursor: 'pointer',
									transition: 'background 0.2s',
								}}
								onMouseOver={(e) => {
									e.currentTarget.style.background = '#e2e8f0';
								}}
								onMouseOut={(e) => {
									e.currentTarget.style.background = '#f1f5f9';
								}}
							>
								Done
							</button>
						</div>
					</div>
				</div>
			</motion.div>
		</div>
	);
}

export default WorkExperienceModal;
